/**
 * Utility functions for formatting time-related values
 */

/**
 * Formats viewer hours into a human-readable string with hours and minutes
 * @param totalViewerHours - The total viewer hours as a decimal number
 * @returns Formatted string like "2h 30m", "45m", or "3h"
 */
export function formatViewerHours(totalViewerHours: number): string {
  const hours = Math.floor(totalViewerHours);
  const minutes = Math.round((totalViewerHours - hours) * 60);

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
}

/**
 * Formats duration in seconds into hours and minutes
 * @param seconds - Duration in seconds
 * @returns Formatted string like "2h 30m", "45m", or "3h"
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
}

/**
 * Formats a timestamp into a relative time string (e.g., "2 hours ago", "3 days ago")
 * @param timestamp - Date object or ISO string
 * @returns Formatted relative time string
 */
export function formatRelativeTime(timestamp: Date | string): string {
  const now = new Date();
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 30) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Converts hours to milliseconds
 * @param hours - Number of hours
 * @returns Milliseconds
 */
export function hoursToMs(hours: number): number {
  return hours * 60 * 60 * 1000;
}

/**
 * Converts milliseconds to hours
 * @param ms - Milliseconds
 * @returns Hours as decimal number
 */
export function msToHours(ms: number): number {
  return ms / (60 * 60 * 1000);
}
